import { useCallback, useRef, useState } from 'react';
import PreAlert from '../../../../../helpers/utils/preAlert';
import { buildPricePreview, readRowsFile } from '../utils/priceSpreadsheet';

const extractErrorMessage = (error, fallback) => {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error) return error;
  return fallback;
};

const useBasePriceImport = ({
  canImport,
  products,
  onShowAlert,
  onSaveRows,
  messages = {},
}) => {
  const inputPriceFile = useRef(null);
  const [loadImportPrices, setLoadImportPrices] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);
  const [openPricePreviewModal, setOpenPricePreviewModal] = useState(false);
  const [pricePreviewRows, setPricePreviewRows] = useState([]);
  const [invalidPriceRows, setInvalidPriceRows] = useState([]);

  const parseErrorMessage = messages.parseError || 'Falha ao processar planilha de preços.';
  const saveErrorMessage = messages.saveError || 'Falha ao atualizar preços.';
  const noUpdatesMessage = messages.noUpdates || 'Nenhum preço precisou ser atualizado.';
  const successMessageBuilder = messages.success || ((count) => `${count} preço(s) atualizado(s) com sucesso.`);

  const clearInputValue = (event) => {
    if (event && event.target && Object.prototype.hasOwnProperty.call(event.target, 'value')) {
      event.target.value = '';
    }
  };

  const handleClickImportPrices = useCallback(() => {
    if (inputPriceFile.current && typeof inputPriceFile.current.click === 'function') {
      inputPriceFile.current.click();
    }
  }, []);

  const handleClosePricePreviewModal = useCallback(() => {
    if (savingPrices) return;
    setOpenPricePreviewModal(false);
    setPricePreviewRows([]);
    setInvalidPriceRows([]);
  }, [savingPrices]);

  const handleImportPriceFile = useCallback(async (event) => {
    if (!canImport) {
      clearInputValue(event);
      return;
    }

    setLoadImportPrices(true);
    try {
      const files = event && event.target && event.target.files ? Array.from(event.target.files) : [];
      const [file] = files;
      if (!file) return;

      const rows = await readRowsFile(file);
      const { updates, invalidRows } = buildPricePreview(rows, products);

      setPricePreviewRows(updates);
      setInvalidPriceRows(invalidRows);
      setOpenPricePreviewModal(true);
    } catch (error) {
      onShowAlert(PreAlert.error(extractErrorMessage(error, parseErrorMessage)));
    } finally {
      setLoadImportPrices(false);
      clearInputValue(event);
    }
  }, [canImport, onShowAlert, parseErrorMessage, products]);

  const handleConfirmPriceUpdate = useCallback(async () => {
    if (!canImport || pricePreviewRows.length === 0) return;

    setSavingPrices(true);
    try {
      const rowsToUpdate = pricePreviewRows.filter((item) => item.newPrice !== item.oldPrice);
      if (rowsToUpdate.length === 0) {
        onShowAlert(PreAlert.success(noUpdatesMessage));
        handleClosePricePreviewModal();
        return;
      }

      await onSaveRows(rowsToUpdate);

      onShowAlert(PreAlert.success(successMessageBuilder(rowsToUpdate.length)));
      handleClosePricePreviewModal();
    } catch (error) {
      onShowAlert(PreAlert.error(extractErrorMessage(error, saveErrorMessage)));
    } finally {
      setSavingPrices(false);
    }
  }, [
    canImport,
    handleClosePricePreviewModal,
    noUpdatesMessage,
    onSaveRows,
    onShowAlert,
    pricePreviewRows,
    saveErrorMessage,
    successMessageBuilder,
  ]);

  return {
    inputPriceFile,
    loadImportPrices,
    savingPrices,
    openPricePreviewModal,
    pricePreviewRows,
    invalidPriceRows,
    setOpenPricePreviewModal,
    handleClickImportPrices,
    handleImportPriceFile,
    handleConfirmPriceUpdate,
    handleClosePricePreviewModal,
  };
};

export default useBasePriceImport;
